import React from 'react';
import { Box, Typography } from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';
import { StylovyzeTooltip } from '@innovyze/stylovyze';
import { useGlobalization } from '@Translations/useGlobalization';
import { RiskCalcType } from './riskSetupDraft.types';

export interface RiskMethodSegmentedControlProps {
	riskType: RiskCalcType;
	disabled: boolean;
	/** Switching is confirmed (and saved) by RiskGrade's existing dialog. */
	onRequestChange: () => void;
}

// Compact segments sized to their content, as in the spike.
const segmentSx = (selected: boolean) => ({
	border: 'none',
	font: 'inherit',
	cursor: 'pointer',
	px: 2.5,
	py: '9px',
	borderRadius: '8px',
	fontSize: 15,
	fontWeight: 700,
	color: selected ? '#0d1520' : '#5b6472',
	background: selected ? '#ffffff' : 'transparent',
	boxShadow: selected ? '0 1px 2px rgba(16,24,40,.12)' : 'none',
	transition: 'all .15s ease',
	'&:disabled': { cursor: 'default', opacity: 0.6 },
});

export const RiskMethodSegmentedControl = ({
	riskType,
	disabled,
	onRequestChange,
}: RiskMethodSegmentedControlProps) => {
	const { t } = useGlobalization();
	const options: { value: RiskCalcType; label: string }[] = [
		{ value: 'linear', label: t('Linear') },
		{ value: 'matrix', label: t('5×5 Matrix') },
	];
	const help = (
		<>
			<b>{t('Linear')}</b>:{' '}
			{t(
				'Combines LoF and CoF into a single risk score, then assigns a risk grade using score thresholds',
			)}
			<br />
			<b>{t('5×5 Matrix')}</b>:{' '}
			{t(
				// eslint-disable-next-line quotes -- apostrophe; prettier requires double quotes here
				"Assigns a risk grade from the matrix position of the LoF and CoF scores. LoF and CoF weighting and the Add/Multiply settings don't apply to this method",
			)}
		</>
	);

	return (
		<Box
			sx={{
				display: 'flex',
				alignItems: 'center',
				gap: 1.25,
				mb: 2.5,
			}}>
			<Typography
				sx={{ fontSize: 17, fontWeight: 700, color: '#0d1520' }}>
				{t('Select risk method')}
			</Typography>
			<Box
				role="group"
				aria-label={t('Risk method')}
				sx={{
					display: 'inline-flex',
					p: '4px',
					gap: '3px',
					borderRadius: '10px',
					background: '#eef1f4',
					border: '1px solid #e3e7ed',
				}}>
				{options.map(option => {
					const selected = option.value === riskType;
					return (
						<Box
							key={option.value}
							component="button"
							type="button"
							data-cy={`risk-method-segment-${option.value}`}
							aria-pressed={selected}
							disabled={disabled}
							onClick={() => {
								if (!selected) onRequestChange();
							}}
							sx={segmentSx(selected)}>
							{option.label}
						</Box>
					);
				})}
			</Box>
			<StylovyzeTooltip title={help} padded>
				<InfoOutlined
					sx={{ fontSize: 16, color: '#b3bac4', cursor: 'default' }}
				/>
			</StylovyzeTooltip>
		</Box>
	);
};
